import React, { useMemo } from "react";
import { Form, Field, useFormik, FormikProvider } from "formik"; // ErrorMessage importado
import { t } from "i18next"; // Assuming t is globally configured

import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { InputWithLeadingIcon } from "@/shared/components/atoms/InputWithLeadingIcon";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { SelectCountry } from "@/shared/components/organisms/SelectCountry";
import { Select } from "@/shared/components/organisms/Select";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAt, faPhone } from "@fortawesome/pro-regular-svg-icons";
import { useBranchsGymQuery } from "@/app/branchs/branchs.api";

import {
  userInitialValues,
  userValidationSchema,
} from "@/shared/schemas/users.schema";
import { toasts } from "@/shared/utils/toasts";

const optionsSex = [
  { id: "Hombre", name: t("user.views.man", "Hombre") },
  { id: "Mujer", name: t("user.views.female", "Mujer") },
];

type RoleOption = { id: string; name: string };
type CountryOption = { id: string; name: string; code: string };
type StateOption = { id: string; name: string };
type GymOption = { id: number; name: string };
type BranchOption = { id: number; name: string; gym_id: number };

export interface UserFormData {
  name: string;
  role: string;
  phone: string;
  email: string;
  address: string;
  country: CountryOption | { id: string; name: string; code: string } | string;
  state: StateOption | { id: string; name: string } | string;
  city: string;
  postal_code: string;
  dni: string;
  occupation: string;
  occupation_description: string;
  company: string;
  birthday: string;
  sex: string;
  objective: string;
  terms: boolean;
  accept_clauses: boolean;
  sufficiently_informed: boolean;
  receive_communications: boolean;
  image_rights: boolean;
  is_chat_responsible: boolean;
  gym_id: number | string;
  branch_id: number | string;
  profile_avatar: File | string | null;
}

interface CreateUserModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentUserFromStore?: any;
  onCreateUser: (values: UserFormData) => Promise<void>;
  isLoadingCreate: boolean;
  countriesData: any[] | undefined;
  statesData: any[] | undefined;
  gymsData: GymOption[] | undefined;
  defaultUser: any;
  defaultGymId: string;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  setOpen,
  onCreateUser,
  isLoadingCreate,
  countriesData,
  statesData,
  defaultUser,
  gymsData,
  defaultGymId,
}) => {
  const formik = useFormik({
    initialValues: {
      ...userInitialValues,
      ...defaultUser,
      role: "Client",
      country: countriesData.find((c) => c.id === "") || {
        id: "",
        name: t("user.views.select_country", "Selecciona un país"),
        code: "",
      },
      state: { id: "", name: "" },
      gym_id: defaultGymId,
      branch_id: "",
      terms:
        userInitialValues.terms !== undefined ? userInitialValues.terms : false,
      accept_clauses:
        userInitialValues.accept_clauses !== undefined
          ? userInitialValues.accept_clauses
          : true,
      sufficiently_informed:
        userInitialValues.sufficiently_informed !== undefined
          ? userInitialValues.sufficiently_informed
          : true,
      receive_communications:
        userInitialValues.receive_communications !== undefined
          ? userInitialValues.receive_communications
          : false,
      image_rights:
        userInitialValues.image_rights !== undefined
          ? userInitialValues.image_rights
          : false,
    },
    enableReinitialize: true,
    validationSchema: userValidationSchema,
    onSubmit: async (values, formikHelpers) => {
      try {
        await onCreateUser(values);
        toasts.success(
          "Usuario creado",
          "Se ha registrado el usuaurio exitosamente"
        );
        formikHelpers.resetForm();
      } catch (error) {
        (console.log("error on onCreateUser"), error);
        toasts.success("Error", "No se ha podido creaer el usuario");
      }
    },
  });

  const countryOptions = useMemo(() => {
    return countriesData.map((country) => ({
      ...country,
      code: country.isoCode,
      name: country.name,
    }));
  }, [countriesData]);

  const stateOptions = useMemo(() => {
    return statesData
      .filter((state) => state.countryCode == formik.values.country.code)
      .map((state) => ({
        ...state,
        code: state.isoCode,
      }));
  }, [statesData, formik.values.country.code]);
  const {
    data: branchesByGym,
    isFetching,
    isLoading,
  } = useBranchsGymQuery({
    branchId: formik.values.gym_id,
  });

  const optionBranches = useMemo(() => {
    if (!branchesByGym) return [];

    return branchesByGym.map((branch) => ({
      ...branch,
      name: branch.branch,
      id: branch.id,
      gym_id: branch.gym_id,
    }));
  }, [branchesByGym]);

  return (
    <FormikProvider value={formik}>
      <Form className="w-full max-w-xl">
        <DialogCreate
          open={open}
          setOpen={setOpen}
          tittle={t(
            "user.views.create_user_profile",
            "Crear Perfil de Usuario"
          )}
          subTittle={t(
            "user.views.fill_user_info",
            "Completa la información del nuevo usuario."
          )}
          tittleButtonCreate={t("general.create", "Crear")}
          tittleButtonCancel={t("general.cancel", "Cancelar")}
          OnclickCreate={() => {
            console.log("Submitting form with values:", formik.values);
            formik.handleSubmit();
          }}
          OnclickCancel={() => {
            setOpen(false);
            formik.resetForm({ values: formik.initialValues });
          }}
          isLoading={isLoadingCreate}
          acceptDisabled={isLoadingCreate || !formik.isValid} // Botón deshabilitado si no es válido
          cancelDisabled={isLoadingCreate}
          // Ajusta dialogWidth si es necesario. Ejemplo:
          //dialogWidth="w-screen sm:max-w-md md:max-w-lg lg:max-w-2xl xl:max-w-3xl"
        >
          {/* Contenedor para centrar el formulario */}
          <div className="w-full flex justify-center py-4 px-2 sm:px-0">
            {" "}
            {/* Añadido padding para el contenido del formulario */}
            <div className="w-full max-w-xl">
              {" "}
              {/* Ajusta max-w-xl según preferencia */}
              <div className="space-y-8">
                {" "}
                {/* Reducido space-y-12 a space-y-8 o space-y-6 */}
                {/* Ya no usamos la estructura de grid md:grid-cols-3 aquí para simplificar y centrar una sola columna de formulario */}
                {/* Sección de campos del formulario */}
                <div className="grid grid-cols-1 gap-y-6 gap-x-4">
                  {" "}
                  {/* Ajustado gap-y-8 a gap-y-6 */}
                  <div className="col-span-full">
                    <InputFile
                      name="profile_avatar"
                      id="profile_avatar"
                      label={t("user.views.avatar", "Avatar")}
                      spanButton={t("user.views.change_avatar", "Cambiar")}
                      description={t(
                        "user.views.upload_avatar_desc",
                        "PNG, JPG, GIF hasta 10MB"
                      )}
                      selectedImage={
                        formik.values.profile_avatar as string | File
                      }
                      onFileChange={(file) =>
                        formik.setFieldValue("profile_avatar", file)
                      }
                      onRemoveImage={() =>
                        formik.setFieldValue("profile_avatar", null)
                      }
                      tittleDelete={t(
                        "general.delete_image",
                        "Eliminar Imagen"
                      )}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.name", "Nombre completo")}
                      span={t("general.mandatory", "Obligatorio")}
                      id="name"
                      name="name"
                      type="text"
                      placeholder={t(
                        "user.views.name_last_placeholder",
                        "Nombre y apellidos"
                      )}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      error={formik.touched.name && !!formik.errors.name}
                      textError={formik.errors.name as string}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={"Role"}
                      name="role"
                      span={t("general.mandatory", "Obligatorio")}
                      type="text"
                      value={formik.values.role}
                      disabled
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithLeadingIcon
                      label={t("user.views.phone", "Teléfono")}
                      span={t("general.optional", "Opcional")}
                      id="phone"
                      name="phone"
                      type="text"
                      placeholder={t(
                        "user.views.phone_placeholder",
                        "Número de teléfono"
                      )}
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      error={formik.touched.phone && !!formik.errors.phone}
                      textError={
                        formik.touched.phone && (formik.errors.phone as string)
                      }
                      icon={
                        <FontAwesomeIcon
                          icon={faPhone}
                          className="h-4 w-4 text-gray-400"
                        />
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithLeadingIcon
                      label={t("user.views.email", "Correo electrónico")}
                      span={t("general.mandatory", "Obligatorio")}
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t(
                        "user.views.email_placeholder",
                        "ejemplo@correo.com"
                      )}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={formik.touched.email && !!formik.errors.email}
                      textError={
                        formik.touched.email && (formik.errors.email as string)
                      }
                      icon={
                        <FontAwesomeIcon
                          icon={faAt}
                          className="h-4 w-4 text-gray-400"
                        />
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.address", "Dirección")}
                      span={t("general.optional", "Opcional")}
                      id="address"
                      name="address"
                      type="text"
                      placeholder={t(
                        "user.views.address_placeholder",
                        "Calle, número, piso"
                      )}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      error={formik.touched.address && !!formik.errors.address}
                      textError={
                        formik.touched.address &&
                        (formik.errors.address as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <SelectCountry
                      span={t("general.optional", "Opcional")}
                      selected={formik.values.country}
                      setSelected={(value) => {
                        formik.setFieldValue("country", value);
                      }}
                      options={countryOptions}
                      tittle={t("user.views.country", "País")}
                    />
                    {formik.touched.country &&
                      formik.errors.country &&
                      typeof formik.errors.country === "string" && (
                        <p className="mt-1 text-sm text-red-600">
                          {formik.errors.country}
                        </p>
                      )}
                  </div>
                  <div className="col-span-full">
                    <Select
                      span={t("general.optional", "Opcional")}
                      selected={formik.values.state}
                      setSelected={(value) =>
                        formik.setFieldValue("state", value)
                      }
                      options={stateOptions}
                      tittle={t("user.views.state", "Estado/Provincia")}
                      disabled={stateOptions?.length === 0}
                    />
                    {formik.touched.state &&
                      formik.errors.state &&
                      typeof formik.errors.state === "string" && (
                        <p className="mt-1 text-sm text-red-600">
                          {formik.errors.state}
                        </p>
                      )}
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.city", "Ciudad")}
                      span={t("general.optional", "Opcional")}
                      id="city"
                      name="city"
                      type="text"
                      placeholder={t("user.views.city_placeholder", "Ciudad")}
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      error={formik.touched.city && !!formik.errors.city}
                      textError={
                        formik.touched.city && (formik.errors.city as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.postal_code", "Código Postal")}
                      span={t("general.optional", "Opcional")}
                      id="postal_code"
                      name="postal_code"
                      type="text"
                      placeholder={t(
                        "user.views.postal_code_placeholder",
                        "Código Postal"
                      )}
                      value={formik.values.postal_code}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.postal_code &&
                        !!formik.errors.postal_code
                      }
                      textError={
                        formik.touched.postal_code &&
                        (formik.errors.postal_code as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.dni", "DNI/Identificación")}
                      span={t("general.optional", "Opcional")}
                      id="dni"
                      name="dni"
                      type="text"
                      placeholder={t(
                        "user.views.dni_placeholder",
                        "Número de identificación"
                      )}
                      value={formik.values.dni}
                      onChange={formik.handleChange}
                      error={formik.touched.dni && !!formik.errors.dni}
                      textError={
                        formik.touched.dni && (formik.errors.dni as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.occupation", "Ocupación")}
                      span={t("general.optional", "Opcional")}
                      id="occupation"
                      name="occupation"
                      type="text"
                      placeholder={t(
                        "user.views.occupation_placeholder",
                        "Profesión"
                      )}
                      value={formik.values.occupation}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.occupation && !!formik.errors.occupation
                      }
                      textError={
                        formik.touched.occupation &&
                        (formik.errors.occupation as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.business", "Empresa")}
                      span={t("general.optional", "Opcional")}
                      id="company"
                      name="company"
                      type="text"
                      placeholder={t(
                        "user.views.business_placeholder",
                        "Nombre de la empresa"
                      )}
                      value={formik.values.company}
                      onChange={formik.handleChange}
                      error={formik.touched.company && !!formik.errors.company}
                      textError={
                        formik.touched.company &&
                        (formik.errors.company as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("user.views.birthday", "Fecha de Nacimiento")}
                      span={t("general.mandatory", "Obligatorio")}
                      id="birthday"
                      name="birthday"
                      type="date"
                      value={formik.values.birthday}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.birthday && !!formik.errors.birthday
                      }
                      textError={
                        formik.touched.birthday &&
                        (formik.errors.birthday as string)
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <Select
                      name="sex"
                      span={t("general.mandatory", "Obligatorio")}
                      error={formik.touched.sex && !!formik.errors.sex}
                      textError={
                        formik.touched.sex && (formik.errors.sex as string)
                      }
                      selected={optionsSex.find(
                        (option) => option.id === formik.values.sex
                      )}
                      setSelected={(value) =>
                        formik.setFieldValue("sex", value.id)
                      }
                      options={optionsSex}
                      tittle={t("user.views.sex", "Sexo")}
                    />
                  </div>
                  <div className="col-span-full">
                    <Select
                      span={t("general.optional", "Opcional")}
                      selected={[
                        {
                          id: "healt",
                          name: t("progress.objective.healt", "Salud"),
                        },
                        {
                          id: "definition",
                          name: t(
                            "progress.objective.definition",
                            "Definición"
                          ),
                        },
                        {
                          id: "hypertrophy",
                          name: t(
                            "progress.objective.hypertrophy",
                            "Hipertrofia"
                          ),
                        },
                      ].find((opt) => opt.id === formik.values.objective)}
                      setSelected={(value) =>
                        formik.setFieldValue("objective", value.id)
                      }
                      options={[
                        {
                          id: "",
                          name: t(
                            "user.views.select_objective",
                            "Selecciona un objetivo"
                          ),
                        },
                        {
                          id: "healt",
                          name: t("progress.objective.healt", "Salud"),
                        },
                        {
                          id: "definition",
                          name: t(
                            "progress.objective.definition",
                            "Definición"
                          ),
                        },
                        {
                          id: "hypertrophy",
                          name: t(
                            "progress.objective.hypertrophy",
                            "Hipertrofia"
                          ),
                        },
                      ]}
                      tittle={t("user.views.objective", "Objetivo")}
                    />
                  </div>
                  <div className="col-span-full">
                    <Select
                      name="gym_id"
                      span={t("general.mandatory", "Obligatorio")}
                      error={formik.touched.gym_id && !!formik.errors.gym_id}
                      textError={
                        formik.touched.gym_id &&
                        (formik.errors.gym_id as string)
                      }
                      selected={
                        formik.values.gym_id
                          ? gymsData.find(
                              (option) => option.id === formik.values.gym_id
                            )
                          : null
                      }
                      setSelected={(value) => {
                        formik.setFieldValue("gym_id", value.id);
                        if (formik.values.branch_id) {
                          //clear branch_id
                          formik.setFieldValue("branch_id", "");
                        }
                      }}
                      options={gymsData}
                      tittle={t("user.views.company", "Gimnasio/Centro")}
                    />
                  </div>
                  <div className="col-span-full">
                    <Select
                      name="branch_id"
                      span={t("general.mandatory", "Obligatorio")}
                      error={
                        formik.touched.branch_id && !!formik.errors.branch_id
                      }
                      textError={
                        formik.touched.branch_id &&
                        (formik.errors.branch_id as string)
                      }
                      selected={
                        formik.values.branch_id
                          ? optionBranches?.find(
                              (option) => option.id === formik.values.branch_id
                            )
                          : null
                      }
                      setSelected={(value) =>
                        formik.setFieldValue("branch_id", value.id)
                      }
                      options={optionBranches}
                      tittle={t("user.views.branch", "Sucursal")}
                      disabled={
                        branchesByGym?.length === 0 || isFetching || isLoading
                      }
                    />
                  </div>
                  {/* Legal Checkboxes */}
                  <div className="col-span-full pt-8 border-t border-gray-200">
                    {" "}
                    {/* Ajustado para consistencia */}
                    <h3 className="text-base font-semibold leading-7 text-gray-900">
                      {t(
                        "user.views.legal_and_communications",
                        "Legal y Comunicaciones"
                      )}
                    </h3>
                    <div className="mt-6 space-y-6">
                      {" "}
                      {/* Agrupado para mejor espaciado */}
                      <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                          <Field
                            as="input"
                            type="checkbox"
                            name="terms"
                            id="terms"
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label
                            htmlFor="terms"
                            className="font-medium text-gray-900 mr-1"
                          >
                            {t(
                              "user.views.accept_term_new_user",
                              "Acepto los términos y condiciones."
                            )}
                          </label>
                          <a
                            href="https://fitmewise.com/terminos-y-condiciones-de-uso/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900"
                          >
                            {t("user.views.ready_more", "Leer más")}
                          </a>
                        </div>
                      </div>
                      <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                          <Field
                            as="input"
                            type="checkbox"
                            name="accept_clauses"
                            id="accept_clauses"
                            disabled={true}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600 disabled:text-primary-300 cursor-not-allowed"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label
                            htmlFor="accept_clauses"
                            className="font-medium text-gray-900"
                          >
                            {t(
                              "user.views.accept_clauses_norms",
                              "Aceptación de cláusulas y normativas del centro."
                            )}
                          </label>
                        </div>
                      </div>
                      <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                          <Field
                            as="input"
                            type="checkbox"
                            name="sufficiently_informed"
                            id="sufficiently_informed"
                            disabled={true}
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600 disabled:text-primary-300 cursor-not-allowed"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label
                            htmlFor="sufficiently_informed"
                            className="font-medium text-gray-900"
                          >
                            {t(
                              "user.views.informed_participation",
                              "He sido suficientemente informado sobre mi participación."
                            )}
                          </label>
                        </div>
                      </div>
                      <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                          <Field
                            as="input"
                            type="checkbox"
                            name="receive_communications"
                            id="receive_communications"
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label
                            htmlFor="receive_communications"
                            className="font-medium text-gray-900"
                          >
                            {t(
                              "user.views.receive_communications_opt_in",
                              "Deseo recibir comunicaciones comerciales y novedades."
                            )}
                          </label>
                        </div>
                      </div>
                      <div className="relative flex items-start">
                        <div className="flex h-6 items-center">
                          <Field
                            as="input"
                            type="checkbox"
                            name="image_rights"
                            id="image_rights"
                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary-600"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label
                            htmlFor="image_rights"
                            className="font-medium text-gray-900"
                          >
                            {t(
                              "user.views.image_rights_opt_in",
                              "Cedo mis derechos de imagen para uso promocional."
                            )}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  {JSON.stringify(formik.errors)}
                </div>
              </div>
            </div>
          </div>
        </DialogCreate>
      </Form>
    </FormikProvider>
  );
};
